import clsx from "clsx";
import { useCallback, useState, useEffect } from "react";
import { JobsEmptyState } from "../../components/Jobs/JobsEmptyState";
import { JobSearchFilters } from "./JobSearchFilters";
import { formatJobDate, formatJobLocation } from "./utils";
import jobApi from "@/api/job";
import userJobApi from "@/api/userJob";
import {
    JobActionsCell,
    JobSourceCell,
    JobTitleCell,
    TableCell,
    TableHeaderCell,
} from "@/components/Jobs/JobTableCells";
import { Pagination } from "@/components/Pagination/Pagination";
import { AppText } from "@/components/Text/AppText";
import { useAppDispatch, useAppSelector } from "@/hooks/types";
import { JobBoardList } from "@/pages/Jobs/JobBoardList";
import {
    getSearchFilters,
    getSearchResults,
    getIsLoading,
} from "@/store/jobSearch/selectors";
import { setSearchFilters, setSearchResults } from "@/store/jobSearch/slice";
import { openModal } from "@/store/modal/slice";
import { getUserJobs } from "@/store/userJob/selectors";
import { addUserJob, deleteUserJob } from "@/store/userJob/slice";
import { IJobListing } from "@/types/job";
import { ModalTypes } from "@/types/modal";

const dashboardColumns = [
    "title",
    "url",
    "date",
    "location",
    "remote",
    "actions",
];

const importFlowColumns = ["title", "url", "date", "location", "remote"];

interface JobBoardTableProps {
    variant?: "dashboard" | "import";
}

export const JobBoardTable = ({
    variant = "dashboard",
}: JobBoardTableProps) => {
    const [loadingJobIds, setLoadingJobIds] = useState<Set<string>>(new Set());
    const [isSearching, setIsSearching] = useState(false);
    const dispatch = useAppDispatch();
    const searchFilters = useAppSelector(getSearchFilters);
    const searchResults = useAppSelector(getSearchResults);
    const isLoading = useAppSelector(getIsLoading);
    const userJobs = useAppSelector(getUserJobs);

    // Determine which columns to use based on variant
    const columns = variant === "import" ? importFlowColumns : dashboardColumns;

    const handleSearch = useCallback(
        (filters: typeof searchFilters) => {
            setIsSearching(true);
            dispatch(setSearchFilters({ ...filters, page: 1 }));
        },
        [dispatch],
    );

    const handlePageChange = useCallback(
        (page: number) => {
            setIsSearching(true);
            dispatch(setSearchFilters({ ...searchFilters, page }));
        },
        [dispatch, searchFilters],
    );

    useEffect(() => {
        const performSearch = async () => {
            if (isSearching && searchFilters.term && searchFilters.location) {
                try {
                    const results = await jobApi.searchJobs(searchFilters);
                    dispatch(setSearchResults(results));
                } catch (error) {
                    console.error("Search failed:", error);
                } finally {
                    setIsSearching(false);
                }
            }
        };

        performSearch();
    }, [isSearching, searchFilters, dispatch]);

    const canRenderColumn = useCallback(
        (column: string) => {
            return columns.includes(column);
        },
        [columns],
    );

    const saveJob = useCallback(
        async (job: IJobListing, isSmartMatch = false) => {
            try {
                setLoadingJobIds(prev => new Set(prev).add(job.id));

                const userJob = userJobs.find(
                    userJob => userJob.jobBoardId === job.id,
                );

                if (isSmartMatch && userJob) {
                    return userJob;
                }

                if (userJob && !isSmartMatch) {
                    await userJobApi.deleteUserJob(userJob.id);
                    dispatch(deleteUserJob(userJob.id));
                    return null;
                }

                const savedJob = await jobApi.saveJob(job);
                dispatch(addUserJob(savedJob));
                return savedJob;
            } catch (error) {
                console.error("Error saving job:", error);
            } finally {
                setLoadingJobIds(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(job.id);
                    return newSet;
                });
            }
        },
        [dispatch, userJobs],
    );

    const openSmartMatchModal = useCallback(
        async (job: IJobListing) => {
            const savedJob = await saveJob(job, true);
            if (!savedJob) {
                return;
            }
            dispatch(
                openModal({
                    modalType: ModalTypes.SMART_MATCH,
                    props: { userJobId: savedJob.id },
                }),
            );
        },
        [dispatch, saveJob],
    );

    return (
        <>
            <JobSearchFilters onSearch={handleSearch} />
            <div className="hidden w-full overflow-x-auto lg:block">
                <table className="w-full">
                    <thead className="text-neutral-50">
                        <tr className="h-8 text-neutral-50">
                            <TableHeaderCell
                                className="w-[200px] rounded-tl-lg"
                                value="Job Title & Company"
                            />

                            <TableHeaderCell
                                className="min-w-[120px]"
                                value="Job Listing"
                            />

                            <TableHeaderCell
                                className="min-w-[120px]"
                                value="Date"
                            />
                            <TableHeaderCell
                                className="min-w-[120px]"
                                value="Location"
                            />

                            <TableHeaderCell
                                className={clsx(
                                    "min-w-[120px]",
                                    !canRenderColumn("actions") &&
                                        "rounded-tr-lg",
                                )}
                                value="Remote"
                            />

                            {canRenderColumn("actions") && (
                                <TableHeaderCell
                                    className="min-w-[100px] rounded-tr-lg"
                                    value="Actions"
                                />
                            )}
                        </tr>
                    </thead>
                    <tbody className="rounded-b-lg border-b border-l border-r border-neutral-200 bg-neutral-50">
                        {isLoading ? (
                            <tr>
                                <td
                                    colSpan={columns.length}
                                    className="p-4 text-center"
                                >
                                    Loading...
                                </td>
                            </tr>
                        ) : (!searchFilters.term && !searchFilters.location) ||
                          searchResults?.jobs.length === 0 ? (
                            <tr className="last:rounded-b-lg">
                                <td
                                    colSpan={columns.length}
                                    className="rounded-b-lg p-4"
                                >
                                    <JobsEmptyState
                                        term={searchFilters.term}
                                        location={searchFilters.location}
                                    />
                                </td>
                            </tr>
                        ) : (
                            searchResults?.jobs.map((job, index, array) => (
                                <tr
                                    key={job.id}
                                    className={clsx(
                                        "bg-neutral-white h-[68px]",
                                        index === array.length - 1
                                            ? ""
                                            : "border-b border-neutral-200",
                                    )}
                                >
                                    <JobTitleCell
                                        isLast={index === array.length - 1}
                                        title={job.title}
                                        organization={job.organization}
                                        organizationLogo={job.organization_logo}
                                    />

                                    <JobSourceCell
                                        source={job.source}
                                        url={job.url}
                                    />
                                    <TableCell center>
                                        <AppText variant="footnote">
                                            {formatJobDate(job.date_posted)}
                                        </AppText>
                                    </TableCell>
                                    <TableCell
                                        center
                                        className="max-w-[200px]"
                                    >
                                        <AppText
                                            variant="footnote"
                                            className="leading-0 !line-clamp-2 block"
                                        >
                                            {formatJobLocation(
                                                job.locations_derived,
                                            )}
                                        </AppText>
                                    </TableCell>
                                    <TableCell center>
                                        <AppText variant="footnote">
                                            {job.remote_derived ? "Yes" : "No"}
                                        </AppText>
                                    </TableCell>
                                    {canRenderColumn("actions") && (
                                        <JobActionsCell
                                            isLoadingActions={loadingJobIds.has(
                                                job.id,
                                            )}
                                            onSaveJob={() => saveJob(job)}
                                            onOpenSmartMatchModal={() =>
                                                openSmartMatchModal(job)
                                            }
                                            isSaved={userJobs.some(
                                                userJob =>
                                                    userJob.jobBoardId ===
                                                    job.id,
                                            )}
                                        />
                                    )}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Mobile View */}
            <JobBoardList
                searchResults={searchResults}
                searchFilters={searchFilters}
                showActions={canRenderColumn("actions")}
                saveJob={saveJob}
                openSmartMatchModal={openSmartMatchModal}
                loadingJobIds={loadingJobIds}
            />

            {/* Pagination */}
            {searchResults && searchResults.jobs.length > 0 && (
                <Pagination
                    currentPage={searchFilters.page}
                    totalPages={Math.ceil(searchResults.total / 10)}
                    onPageChange={handlePageChange}
                    variant="minimal"
                    maxVisiblePages={3}
                    className="justify-center"
                />
            )}
        </>
    );
};
